/**
 * Vue shell for change-history review and trash-file preview.
 *
 * TEA: pure Model/Msg in `useHistoryReview.update.ts`; showing / hiding the
 * review, panel open and layout stay here as Cmds. Both render in a second,
 * read-only ProseMirror (`prose.show`, #252): a history diff as plain text
 * with insert / delete marks, a trash preview as the document itself (a
 * pre-conversion Markdown file is converted for display). The live editor
 * stays mounted underneath, hidden and read-only.
 */
import { computed, nextTick, watch, type ComputedRef, type Ref } from "vue";
import type { HistorySelection } from "../components/ChangeHistoryPanel.selection";
import { projectRelativePath } from "../lib/fileHistory";
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { useTea } from "../lib/tea";
import type { EditorKind } from "../lib/tauri/core";
import type { HistoryRange } from "../lib/proseDiff";
import {
  plainTextToZwDoc,
  previewAsZwDoc,
  restoreIntoZwDoc,
} from "../lib/zwdoc/review";
import {
  initialHistoryReviewModel,
  updateHistoryReviewFn,
  type HistoryReviewCmd,
  type HistoryTrashPreview,
} from "./useHistoryReview.update";

export type TrashPreviewState = Maybe<HistoryTrashPreview>;

/** What the read-only review shows: `.zwdoc` text + history marks. */
export type ProseReviewDoc = {
  text: string;
  ranges: readonly HistoryRange[];
};

export function useHistoryReview(options: {
  /** Written synchronously when entering/leaving review so the editor sees
   * review mode in the same turn as the swap. */
  reviewModeActive: Ref<boolean>;
  doc: Ref<string>;
  mode: Ref<EditorKind>;
  rootPath: Ref<Maybe<string>>;
  activePath: Ref<Maybe<string>>;
  activeFile: Ref<Maybe<string>>;
  activeNote: Ref<Maybe<string>>;
  activeDatabase: Ref<Maybe<string>>;
  historyOpen: Ref<boolean>;
  mobileShell: Ref<boolean>;
  commentsOpen: Ref<boolean>;
  bubbleVisible: Ref<boolean>;
  proseMotionReady: Ref<boolean>;
  layoutInstant: Ref<boolean>;
  scheduleLayout: () => void;
  onTrashPreviewChange: (preview: TrashPreviewState) => void;
  onRestoreVersion: (content: string) => Promise<void> | void;
  /** Show (or with Nothing, hide) the read-only review. */
  showReview: (review: Maybe<ProseReviewDoc>) => void;
}): {
  historySelection: Ref<Maybe<HistorySelection>>;
  historyReviewActive: ComputedRef<boolean>;
  trashPreview: Ref<TrashPreviewState>;
  trashPreviewActive: ComputedRef<boolean>;
  reviewModeActive: ComputedRef<boolean>;
  historyRelativePath: ComputedRef<Maybe<string>>;
  historyFileLabel: ComputedRef<Maybe<string>>;
  canShowHistory: ComputedRef<boolean>;
  applyHistorySelection: (selectionM: Maybe<HistorySelection>) => void;
  exitHistoryReview: (restoreLiveDoc?: boolean) => void;
  showTrashPreview: (content: string, label: string, path: string) => void;
  clearTrashPreview: (restoreLiveDoc?: boolean) => void;
  onHistorySelect: (selectionM: Maybe<HistorySelection>) => void;
  onHistoryRestore: (content: string) => void;
  toggleHistory: () => void;
  closeHistory: () => void;
} {
  function syncReviewModeBridge(): void {
    options.reviewModeActive.value =
      (options.historyOpen.value === true &&
        isJust(model.value.historySelection)) ||
      isJust(model.value.trashPreview);
  }

  function show(review: ProseReviewDoc): void {
    options.showReview(just(review));
    options.scheduleLayout();
  }

  function hide(): void {
    options.showReview(nothing);
    options.scheduleLayout();
  }

  function applySelectionToEditor(selection: HistorySelection): void {
    show({
      text: plainTextToZwDoc(selection.reviewText),
      ranges: selection.ranges,
    });
  }

  function showTrashPreviewEffects(preview: HistoryTrashPreview): void {
    const text = previewAsZwDoc(preview.content);
    show({
      text: text.ok ? text.value : plainTextToZwDoc(preview.content),
      ranges: [],
    });
  }

  /** History closed: back to the trash preview it was layered on, or the live doc. */
  function exitHistoryReviewEffects(trashStillActive: boolean): void {
    const trashM = model.value.trashPreview;
    if (trashStillActive === true && isJust(trashM)) {
      showTrashPreviewEffects(trashM.value);
    } else {
      hide();
    }
  }

  function applyRestoredDoc(content: string): void {
    options.doc.value = content;
    options.scheduleLayout();
  }

  function runCmd(cmd: HistoryReviewCmd): void {
    switch (cmd.kind) {
      case "syncReviewModeBridge":
        syncReviewModeBridge();
        return;
      case "applySelectionToEditor":
        applySelectionToEditor(cmd.selection);
        return;
      case "exitHistoryReviewEffects":
        exitHistoryReviewEffects(cmd.trashStillActive);
        return;
      case "showTrashPreviewEffects":
        showTrashPreviewEffects(cmd.preview);
        return;
      case "clearTrashPreviewEffects":
        hide();
        return;
      case "setHistoryOpen":
        options.historyOpen.value = cmd.open;
        return;
      case "closeMobileChrome":
        options.commentsOpen.value = false;
        options.bubbleVisible.value = false;
        return;
      case "notifyTrashPreviewChange":
        options.onTrashPreviewChange(cmd.preview);
        return;
      case "restoreVersion":
        void options.onRestoreVersion(cmd.content);
        return;
      case "applyRestoredDoc":
        applyRestoredDoc(cmd.content);
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea(
    initialHistoryReviewModel,
    updateHistoryReviewFn,
    { runCmd: just(runCmd) },
  );

  const historySelection = computed(() => model.value.historySelection);
  const trashPreview = computed(() => model.value.trashPreview);

  const historyRelativePath = computed((): Maybe<string> => {
    const trashPath = matchMaybe(trashPreview.value, {
      nothing: () => nothing as Maybe<string>,
      just: (p) => (p.path.length > 0 ? just(p.path) : nothing),
    });
    if (isJust(trashPath)) return trashPath;
    const rootM = options.rootPath.value;
    const activeM = options.activePath.value;
    if (isNothing(rootM) || isNothing(activeM)) return nothing;
    return projectRelativePath(rootM.value, activeM.value);
  });

  const historyFileLabel = computed((): Maybe<string> => {
    const fromTrash = matchMaybe(trashPreview.value, {
      nothing: () => nothing as Maybe<string>,
      just: (p) => just(p.label),
    });
    if (isJust(fromTrash)) return fromTrash;
    if (isJust(options.activeFile.value)) return options.activeFile.value;
    if (isJust(options.activeNote.value)) return options.activeNote.value;
    if (isJust(options.activeDatabase.value))
      return options.activeDatabase.value;
    return nothing;
  });

  const canShowHistory = computed(
    () =>
      options.mode.value === "project" &&
      isJust(options.rootPath.value) &&
      isJust(historyRelativePath.value),
  );

  const historyReviewActive = computed(
    () =>
      options.historyOpen.value === true &&
      isJust(model.value.historySelection),
  );
  const trashPreviewActive = computed(() => isJust(model.value.trashPreview));
  const reviewModeActive = computed(
    () =>
      historyReviewActive.value === true || trashPreviewActive.value === true,
  );

  function applyHistorySelection(selectionM: Maybe<HistorySelection>): void {
    void dispatch({ type: "ApplyHistorySelection", selection: selectionM });
  }

  function exitHistoryReview(restoreLiveDoc = true): void {
    void dispatch({ type: "ExitHistoryReview", restoreLiveDoc });
  }

  function showTrashPreview(
    content: string,
    label: string,
    path: string,
  ): void {
    void dispatch({ type: "ShowTrashPreview", content, label, path });
  }

  function clearTrashPreview(restoreLiveDoc = true): void {
    void dispatch({
      type: "ClearTrashPreview",
      restoreLiveDoc,
      historyOpen: options.historyOpen.value,
    });
  }

  function onHistorySelect(selectionM: Maybe<HistorySelection>): void {
    void dispatch({
      type: "HistorySelect",
      selection: selectionM,
      historyOpen: options.historyOpen.value,
    });
  }

  function onHistoryRestore(content: string): void {
    // A version from before conversion restores as `.zwdoc`.
    const restored = restoreIntoZwDoc(options.doc.value, content);
    void dispatch({ type: "HistoryRestore", content: restored });
  }

  function toggleHistory(): void {
    void dispatch({
      type: "ToggleHistory",
      canShow: canShowHistory.value,
      mobileShell: options.mobileShell.value,
      historyOpen: options.historyOpen.value,
    });
  }

  function closeHistory(): void {
    void dispatch({ type: "CloseHistory" });
  }

  // Switching files drops any open review.
  watch(
    () => [options.activePath.value, options.rootPath.value] as const,
    () => {
      void dispatch({
        type: "FileSwitched",
        canShowHistory: canShowHistory.value,
        historyOpen: options.historyOpen.value,
      });
    },
  );

  // Snap layout (no easing) around the history panel opening/closing.
  watch(
    options.historyOpen,
    (open) => {
      const restoreMotion = options.proseMotionReady.value;
      options.proseMotionReady.value = false;
      options.layoutInstant.value = true;
      if (open === false) exitHistoryReview();
      options.scheduleLayout();
      void nextTick(() => {
        options.scheduleLayout();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            options.proseMotionReady.value = restoreMotion;
            options.layoutInstant.value = false;
          });
        });
      });
    },
    { immediate: true },
  );

  return {
    historySelection,
    historyReviewActive,
    trashPreview,
    trashPreviewActive,
    reviewModeActive,
    historyRelativePath,
    historyFileLabel,
    canShowHistory,
    applyHistorySelection,
    exitHistoryReview,
    showTrashPreview,
    clearTrashPreview,
    onHistorySelect,
    onHistoryRestore,
    toggleHistory,
    closeHistory,
  };
}
